import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Form, Table, Dropdown, Image } from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { IconArrowRight } from "@tabler/icons-react";
import PageHeader from "../../components/header/PageHeader";
import { useToast } from "../../components/toast";
import ConfirmModal from "../../components/modal/ConfirmModal";
import api from "../../api";
import ImageUpload from "../../components/image-upload/ImageUpload";
import { formatDate } from "../../utils/helper";

// ─── Main component ───────────────────────────────────
export default function Issuers() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  // ─── Fetch ───────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    setLoading(true);
    const res = await api.issuers_list({ page, limit: 10 });
    if (res?.status === 200) {
      setRows(res.data.data);
      setMeta(res.data.meta);
    } else {
      toast("Өгөгдөл авахад алдаа гарлаа", "error");
    }
    setLoading(false);
  }, [page]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) => r.name?.toLowerCase().includes(qq));
  }, [rows, q]);

  // ─── Create ──────────────────────────────────────────
  const onCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      const res = await api.issuers_create({
        name: values.name.trim(),
        logoUrl: values.logoUrl || null,
      });
      if (res?.status === 201) {
        toast("Амжилттай нэмэгдлээ", "success");
        createForm.resetFields();
        setCreateOpen(false);
        fetchRows();
      } else {
        toast(res?.data?.message || "Нэмэхэд алдаа гарлаа", "error");
      }
    } catch {
    } finally {
      setCreating(false);
    }
  };

  // ─── Edit ────────────────────────────────────────────
  const openEdit = (record) => {
    setEditingRow(record);
    editForm.setFieldsValue({
      name: record.name,
      logoUrl: record.logoUrl || null,
    });
    setEditOpen(true);
  };

  const onUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      setUpdating(true);
      const res = await api.issuers_update(editingRow.id, {
        name: values.name.trim(),
        logoUrl: values.logoUrl || null,
      });
      if (res?.status === 200) {
        toast("Амжилттай засагдлаа", "success");
        setRows((prev) =>
          prev.map((r) => (r.id === editingRow.id ? { ...r, ...res.data } : r)),
        );
        setEditOpen(false);
        setEditingRow(null);
      } else {
        toast(res?.data?.message || "Засахад алдаа гарлаа", "error");
      }
    } catch {
    } finally {
      setUpdating(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────
  const onDelete = async () => {
    const res = await api.issuers_delete(deleteModal.id);
    if (res?.status === 200) {
      toast("Устгагдлаа", "info");
      setRows((prev) => prev.filter((r) => r.id !== deleteModal.id));
    } else {
      toast(res?.data?.message || "Устгахад алдаа гарлаа", "error");
    }
    setDeleteModal({ open: false, id: null });
  };

  // ─── Columns ─────────────────────────────────────────
  const columns = [
    {
      title: "Лого",
      dataIndex: "logoUrl",
      width: 100,
      render: (v) =>
        v ? (
          <Image
            src={v}
            width={40}
            height={40}
            style={{ objectFit: "contain", borderRadius: 4 }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 4,
              background: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PictureOutlined style={{ color: "#bbb" }} />
          </div>
        ),
    },
    {
      title: "Нэр",
      dataIndex: "name",
      render: (v) => <div className="fw-semibold">{v}</div>,
    },
    // {
    //   title: "Карт тоо",
    //   dataIndex: "_count",
    //   render: (v) => v?.cards ?? 0,
    // },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      render: (v) => formatDate(v),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: <span style={{ fontSize: 13 }}>Засах</span>,
              },
              {
                key: "delete",
                label: (
                  <span style={{ fontSize: 13, color: "#ff4d4f" }}>Устгах</span>
                ),
              },
            ],
            onClick: ({ key }) => {
              if (key === "edit") openEdit(record);
              if (key === "delete")
                setDeleteModal({ open: true, id: record.id });
            },
          }}
          trigger={["click"]}
        >
          <Button variant="filled" color="default" style={{ fontSize: 12 }}>
            Үйлдэл <IconArrowRight size={12} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  // ─── Shared form content ──────────────────────────────
  const FormFields = () => (
    <>
      <Form.Item
        label="Нэр"
        name="name"
        rules={[{ required: true, message: "Нэрээ оруулна уу" }]}
      >
        <Input placeholder="Жишээ: Khan Bank" />
      </Form.Item>

      <Form.Item label="Лого" name="logoUrl">
        <ImageUpload size={100} />
      </Form.Item>
    </>
  );

  return (
    <section className="staff-section">
      <PageHeader
        title="Байгууллага"
        sub="Системд бүртгэлтэй байгууллагуудын жагсаалт"
        rSide={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setCreateOpen(true);
            }}
          >
            Нэмэх
          </Button>
        }
      />

      <div className="toolbar">
        <Input
          className="search"
          prefix={<SearchOutlined />}
          placeholder="Нэрээр хайх..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          allowClear
        />
      </div>

      <div className="table-wrap">
        <Table
          rowKey="id"
          loading={{ spinning: loading, size: "small" }}
          columns={columns}
          dataSource={filtered}
          pagination={{
            current: meta?.page,
            pageSize: meta?.limit,
            total: meta?.total,
            onChange: (p) => setPage(p),
          }}
        />
      </div>

      {/* Create Modal */}
      <Modal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        okText="Нэмэх"
        cancelText="Болих"
        confirmLoading={creating}
        title="Байгууллага нэмэх"
      >
        <Form form={createForm} layout="vertical">
          <FormFields />
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingRow(null);
        }}
        onOk={onUpdate}
        okText="Хадгалах"
        cancelText="Болих"
        confirmLoading={updating}
        title="Байгууллага засах"
      >
        <Form form={editForm} layout="vertical">
          <FormFields />
        </Form>
      </Modal>

      <ConfirmModal
        showModal={deleteModal.open}
        setShowModal={(v) => setDeleteModal((prev) => ({ ...prev, open: v }))}
        submitHandler={onDelete}
        title="Итгэлтэй байна уу?"
        description="Энэ байгууллагыг устгахад карт холбоотой байвал устгах боломжгүй."
        variant="error"
      />
    </section>
  );
}
